import React, { useEffect, useRef, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { Button, Dialog, DialogContent, IconButton, LinearProgress, Paper } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import UndoIcon from '@material-ui/icons/Undo';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { undoConfig, redoConfig } from '../../api/searchExperienceSlice';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import LayoutEditor from '../properties/LayoutEditor';
import LessonCoach from './LessonCoach';
import { createLessonStore, LESSON_STEPS } from './lessonState';
import styles from './Onboarding.module.scss';
import useLessonGuide from './useLessonGuide';

const LessonContent = ({ initialStep, onProgress, onClose, onRestart }) => {
  const t = useLocalizedMessage();
  const dispatch = useDispatch();
  const rootRef = useRef(null);
  const state = useSelector(value => value.searchExperience);
  const { step, complete, missing, instruction, resolveTarget, resolveDropTarget, onInteraction, canFinish, finish } = useLessonGuide({ initialStep, state, t });
  useEffect(() => { onProgress(step); }, [step, onProgress]);
  const undo = () => dispatch(undoConfig());
  const progressLabel = t('experience.lesson.progress', { current: Math.min(step + 1, LESSON_STEPS.length), total: LESSON_STEPS.length });

  return <div ref={rootRef} className={styles.lesson} data-testid="editor-lesson" data-step={step}
    onKeyDown={event => {
      // Events from a portal still bubble to the live editor. Keep all practice
      // shortcuts inside this store, especially Cmd/Ctrl+S and Cmd/Ctrl+Z.
      event.stopPropagation();
      if (event.key === 'Escape') { onClose(); return; }
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key.toLowerCase() === 's') event.preventDefault();
      if (event.key.toLowerCase() === 'z' && !event.target.closest('input, textarea, [contenteditable="true"]')) {
        event.preventDefault();
        if (event.shiftKey) dispatch(redoConfig()); else undo();
      }
    }}>
    <header className={styles.lessonHeader}>
      <div><h2 id="editor-lesson-title">{t('experience.lesson.title')}</h2><p>{t('experience.lesson.safe')}</p></div>
      <Button onClick={onRestart}>{t('experience.lesson.restart')}</Button>
      <Button onClick={onClose}>{t('experience.lesson.finishLater')}</Button>
      <IconButton onClick={onClose} aria-label={t('experience.lesson.close')}><CloseIcon /></IconButton>
    </header>
    <LinearProgress variant="determinate" value={step / LESSON_STEPS.length * 100} aria-label={t('experience.lesson.progressLabel')} />
    {complete ? <div className={styles.completed} role="status">
      <CheckCircleOutlineIcon /><h3>{t('experience.lesson.completedTitle')}</h3><p>{t('experience.lesson.completedBody')}</p>
      <Paper variant="outlined" className={styles.saveExplanation}><h4>{t('experience.lesson.savingTitle')}</h4><p>{t('experience.lesson.savingBody')}</p></Paper>
      <Button onClick={onClose}>{t('experience.lesson.return')}</Button>
    </div> : <>
      <div className={styles.stepBar} aria-live="polite">
        <div><small>{progressLabel}</small><strong>{t(`experience.lesson.${instruction}.title`)}</strong><p>{t(`experience.lesson.${instruction}.body`, { text: t('experience.lesson.copyText'), sampleText: t('experience.lesson.sampleText') })}</p></div>
        <Button data-lesson-undo variant="outlined" startIcon={<UndoIcon />} disabled={!state.editorHistory.past.length} onClick={undo}>{t('experience.design.undo')}</Button>
        {canFinish && <Button data-lesson-finish variant="contained" color="primary" onClick={finish}>{t('experience.lesson.finish')}</Button>}
      </div>
      <div className={styles.practice}>
        <LayoutEditor shopId="learning-example" onInteraction={onInteraction} />
      </div>
      {!missing && <LessonCoach rootRef={rootRef} resolveTarget={resolveTarget} resolveDropTarget={resolveDropTarget} progress={progressLabel}
        title={t(`experience.lesson.${instruction}.title`)} description={t(`experience.lesson.${instruction}.body`, { text: t('experience.lesson.copyText'), sampleText: t('experience.lesson.sampleText') })} />}
    </>}
  </div>;
};

const EditorLesson = ({ initialStep = 0, onProgress, onClose, onRestart }) => {
  const t = useLocalizedMessage();
  const [store] = useState(() => createLessonStore(initialStep, t));
  return <Dialog open fullScreen onClose={onClose} aria-labelledby="editor-lesson-title" onKeyDown={event => event.stopPropagation()}>
    <DialogContent className={styles.dialogContent}>
      <Provider store={store}>
        <LessonContent initialStep={initialStep} onProgress={onProgress} onClose={onClose} onRestart={onRestart} />
      </Provider>
    </DialogContent>
  </Dialog>;
};
export default EditorLesson;
